import { JWTService } from './jwtService';

/**
 * Supplies the Supabase access token for every request.
 *
 * The GoIcon JWT is exchanged server-side (netlify/functions/auth-exchange) for a
 * Supabase token carrying facility_id / user_role claims, which RLS policies read
 * via auth.jwt(). Without a token, requests fall back to the anon key and RLS
 * denies them — that is the intended behaviour, not an error to paper over.
 */

interface CachedToken {
  token: string;
  /** Unix seconds. */
  expiresAt: number;
}

// Refresh slightly early so a request never travels with an almost-expired token.
const REFRESH_MARGIN_SECONDS = 60;

let cached: CachedToken | null = null;
/** Shared across concurrent callers so a burst of queries triggers one exchange. */
let inFlight: Promise<string | null> | null = null;

function isUsable(entry: CachedToken | null): entry is CachedToken {
  if (!entry) return false;
  return entry.expiresAt - REFRESH_MARGIN_SECONDS > Math.floor(Date.now() / 1000);
}

function exchangeEndpoint(): string {
  const explicitBase = process.env.REACT_APP_FUNCTIONS_BASE;
  if (explicitBase) {
    return `${explicitBase.replace(/\/$/, '')}/auth-exchange`;
  }
  return '/.netlify/functions/auth-exchange';
}

async function requestExchange(goiconToken: string): Promise<string | null> {
  try {
    const response = await fetch(exchangeEndpoint(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ token: goiconToken }),
    });

    if (!response.ok) {
      console.error(`❌ Supabase token exchange failed (${response.status})`);
      return null;
    }

    const data = await response.json();
    if (!data?.token || !data?.expiresAt) {
      console.error('❌ Supabase token exchange returned an unexpected payload');
      return null;
    }

    cached = { token: data.token, expiresAt: data.expiresAt };
    return cached.token;
  } catch (error) {
    console.error('❌ Supabase token exchange error:', error);
    return null;
  }
}

/**
 * Returns a valid Supabase access token, exchanging the GoIcon JWT if needed.
 * Concurrent callers share a single in-flight exchange.
 */
export async function getSupabaseToken(): Promise<string | null> {
  if (isUsable(cached)) return cached.token;
  if (inFlight) return inFlight;

  const goiconToken = JWTService.getToken();
  if (!goiconToken) {
    // No identity to exchange. Requests proceed unauthenticated and RLS refuses.
    return null;
  }

  inFlight = requestExchange(goiconToken).finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/** Drops the cached token, forcing a fresh exchange on the next request. */
export function clearSupabaseToken(): void {
  cached = null;
  inFlight = null;
}
